import React, { useCallback } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { useTranslation } from '@lib/useTypedTranslation';

import { Loading } from '../../../../components/loading/loading';
import { TimePeriodAndFilterPicker } from '../../../../components/timePeriodAndFilterPicker/timePeriodAndFilterPicker';
import { useFilterPicker } from '../../../../components/timePeriodAndFilterPicker/useFilterPicker';
import { useTimePeriodPicker } from '../../../../components/timePeriodAndFilterPicker/useTimePeriodPicker';
import { useWorldAction } from '../../../../lib/useWorldAction';
import { useWorldRequest } from '../../../../lib/useWorldRequest';
import { exportDeviceEventChartAction, getCorePerformanceEvent } from '../../../../services/core/performance';
import { getFormattedTitle } from '../../performance/charts/lib';
import { ProcessCrashesChart } from './processCrashesChart';
import { getProcessCrashesHeading, ProcessCrashesSubheading } from './processCrashesSubheading';
import { getProcessCrashesParams, useProcessCrashesScope } from './useProcessCrashesScope';

const timePeriods = [30, 60, 90];

const TimePeriodAndFilterSelection = styled(TimePeriodAndFilterPicker)`
  padding-top: 1rem;
  border-bottom: 1px solid #d7e2eb;
` as typeof TimePeriodAndFilterPicker;

const Section = styled.div`
  padding-bottom: 0.625rem;
  margin: 1.5rem 2.5rem 0;
  position: relative;
`;

export function ProcessCrashes() {
  const { t } = useTranslation(['coreProcesses', 'filters', 'timeState']);

  const { filter, optionsLoading, ...filterPickerProps } = useFilterPicker({ includeModels: true });
  const { timePeriod, ...timePeriodPickerProps } = useTimePeriodPicker({ timePeriods });
  const { scope, selectedDay, toggleDay } = useProcessCrashesScope({ days: timePeriod.value, filter });

  // Selecting a day doesn't change the scope, so doesn't reload the chart - it always shows the whole time period
  const dataFetcher = useCallback(() => getCorePerformanceEvent(getProcessCrashesParams(scope)), [scope]);
  const { data, loading } = useWorldRequest(dataFetcher, { initialData: [], initialLoading: true });

  // The export is of the scope the chart is displaying (not one recalculated when the button is clicked), and is of the
  // whole time period, whether or not a day is selected
  const exportProcessCrashes = useWorldAction(exportDeviceEventChartAction);
  const onExportClicked = (): Promise<string> => exportProcessCrashes(getProcessCrashesParams(scope));
  const exportTitle = getProcessCrashesHeading({ filter: scope.filter, days: scope.days, timeZone: scope.timeZone, t });

  return (
    <div data-id="process-crashes">
      <TimePeriodAndFilterSelection
        filter={filter}
        {...filterPickerProps}
        timePeriod={timePeriod}
        {...timePeriodPickerProps}
      />
      <Loading isLoading={loading || optionsLoading} transparentOverlay={false}>
        <Section>
          <ProcessCrashesChart
            data={data}
            scope={scope}
            filename={`${getFormattedTitle(exportTitle)}.${moment.utc().toISOString()}.csv`}
            onExportClicked={onExportClicked}
            onDayClick={toggleDay}
            selectedDay={selectedDay}
          />
        </Section>
        <Section>
          <ProcessCrashesSubheading
            filter={scope.filter}
            days={scope.days}
            timeZone={scope.timeZone}
            selectedDay={selectedDay}
          />
        </Section>
      </Loading>
    </div>
  );
}
